import type {
  DeviceAssessment,
  ModelGroupConfiguration,
  PickupAvailability,
  PriceQuote,
} from '../api/client'

/**
 * Everything one sale consists of, and the reducer that moves it forward.
 *
 * The state is deliberately small and fully serialisable: it is written to localStorage after every
 * change and read back on load, which is what makes a refresh, an accidental tab close, or a trip to
 * another app to fetch an OTP harmless. Anything that can be fetched again - the assessment, the
 * category list - is NOT part of it, because a stale copy of the catalogue is worse than a second call.
 */

/** Where the conversation has got to. The order here is the order of the sale. */
export type SaleStep =
  | 'category'
  | 'model'
  | 'configuration'
  | 'components'
  | 'age'
  | 'condition'
  | 'faults'
  | 'accessories'
  | 'pincode'
  | 'quote'
  | 'identify'
  | 'confirm'
  | 'done'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
}

export interface ChosenDevice {
  deviceConfigId: number
  name: string
  brand?: string
  category?: string
  /** The model group, when this device is one configuration of several. */
  modelGroupId?: number
  /** How many priced configurations the group has. More than one is a question still to ask. */
  configurationCount?: number
}

export interface SaleAnswers {
  deviceVariantId?: number
  durationTimeId?: number
  conditionIds: number[]
  faultIds: number[]
  accessoryIds: number[]
  /** True only once the accessory question has actually been answered - it changes the price. */
  accessoriesAssessed: boolean
  /**
   * The components the customer says the device ACTUALLY has, when they differ from the configuration it
   * shipped as. Optional by design: leaving them unset means "as listed", which is the common case.
   */
  ramId?: number
  processorId?: number
  hardDriveId?: number
  screenSizeId?: number
}

/** The fields a quote was priced from, and therefore the fields the lead must be created from. */
export interface PricedSelection {
  deviceConfigId: number
  deviceVariantId?: number
  durationTimeId?: number
  deviceConditionIds?: number[]
  functionalIssueIds?: number[]
  accessoryIds?: number[]
  accessoriesAssessed?: boolean
  processorId?: number
  ramId?: number
  hardDriveId?: number
  screenSizeId?: number
  promoCode?: string
}

export interface SaleState {
  step: SaleStep
  messages: ChatMessage[]
  categoryId?: number
  categoryName?: string
  device?: ChosenDevice
  /** Catalogue matches the customer still has to choose between. Cleared once one is picked. */
  candidates: ChosenDevice[]
  /**
   * The RAM/storage configurations of the chosen model group, when it has more than one.
   *
   * For phones and tablets each configuration is a separate catalogue device, so the search returns only
   * ONE of them. Without asking, a sale silently priced whichever the search happened to land on - an
   * iPhone 15 256 GB seller was quoted the 128 GB price. Cleared as soon as one is chosen.
   */
  configurations: ModelGroupConfiguration[]
  /**
   * Catalogue names close to a search that found nothing - "do you mean Dell Latitude?".
   *
   * Cashit's search needs every word to match, so one mistyped letter returned nothing at all and the
   * customer was told their device is not in the catalogue. These are a QUESTION, never a substitution:
   * picking one re-runs the real search with that name.
   */
  suggestions: string[]
  answers: SaleAnswers
  pincode?: string
  pickup?: PickupAvailability
  quote?: PriceQuote
  /**
   * The EXACT selection <see cref="quote"/> was priced from, as the server reported it back.
   *
   * The lead is created from this, never from the answers this client believes were given. On the guided
   * path the two are the same thing. On the assistant path they are not: the assistant gathers the answers
   * and sends them to Cashit itself, so the browser never sees them - and creating the lead from the
   * browser's own (empty) idea of the selection would price the quote one way and the lead another. The
   * customer would be shown one figure and sell at a different one.
   */
  pricedSelection?: PricedSelection
  /** Generated once when the confirmation step is reached, so every retry of that submission is one lead. */
  idempotencyKey?: string
  leadNumber?: string
  /** Set when a step failed, so the page can show it without losing anything the customer answered. */
  error?: string
}

export const emptyAnswers: SaleAnswers = {
  conditionIds: [],
  faultIds: [],
  accessoryIds: [],
  accessoriesAssessed: false,
}

export const initialSale: SaleState = {
  step: 'category',
  messages: [],
  candidates: [],
  configurations: [],
  suggestions: [],
  answers: { ...emptyAnswers },
}

export type SaleAction =
  | { type: 'reset' }
  | { type: 'restore'; state: SaleState }
  | { type: 'say'; role: 'user' | 'assistant'; text: string }
  | { type: 'category'; id: number; name: string }
  | { type: 'candidates'; devices: ChosenDevice[] }
  | { type: 'configurations'; options: ModelGroupConfiguration[] }
  | { type: 'suggestions'; names: string[] }
  | { type: 'device'; device: ChosenDevice }
  | { type: 'configuration'; deviceVariantId: number }
  | { type: 'age'; durationTimeId: number }
  | { type: 'conditions'; ids: number[] }
  | { type: 'faults'; ids: number[] }
  | { type: 'accessories'; ids: number[] }
  | { type: 'components'; ramId?: number; processorId?: number; hardDriveId?: number; screenSizeId?: number }
  | { type: 'pincode'; pincode: string; pickup?: PickupAvailability }
  | { type: 'quote'; quote: PriceQuote; selection: PricedSelection }
  | { type: 'step'; step: SaleStep }
  | { type: 'idempotencyKey'; key: string }
  | { type: 'lead'; leadNumber: string }
  | { type: 'error'; message?: string }

let messageCounter = 0
const nextMessageId = () => `m${Date.now().toString(36)}-${messageCounter++}`

export function saleReducer(state: SaleState, action: SaleAction): SaleState {
  switch (action.type) {
    case 'reset':
      return { ...initialSale, answers: { ...emptyAnswers } }

    case 'configurations':
      return { ...state, configurations: action.options, step: 'configuration', error: undefined }

    case 'suggestions':
      return { ...state, suggestions: action.names, step: 'model', error: undefined }

    case 'restore':
      return action.state

    case 'say':
      return {
        ...state,
        messages: [...state.messages, { id: nextMessageId(), role: action.role, text: action.text }],
      }

    case 'category':
      // Changing the category invalidates the device and every answer under it: keeping a condition id
      // chosen for a phone while selling a television would submit an id the server would refuse, and
      // silently re-using an age bracket across categories would change the price for no stated reason.
      return {
        ...state,
        categoryId: action.id,
        categoryName: action.name,
        device: undefined,
        candidates: [],
        configurations: [],
        suggestions: [],
        answers: { ...emptyAnswers },
        quote: undefined,
        pricedSelection: undefined,
        step: 'model',
        error: undefined,
      }

    case 'candidates':
      return { ...state, candidates: action.devices, suggestions: [], step: 'model', error: undefined }

    case 'device':
      // Same reasoning as a category change: a new model means new applicable questions.
      return {
        ...state,
        device: action.device,
        candidates: [],
        configurations: [],
        suggestions: [],
        answers: { ...emptyAnswers },
        quote: undefined,
        pricedSelection: undefined,
        step: 'configuration',
        error: undefined,
      }

    case 'configuration':
      return {
        ...state,
        answers: { ...state.answers, deviceVariantId: action.deviceVariantId },
        quote: undefined,
        pricedSelection: undefined,
        // 'components', not 'age': a laptop or desktop is then offered the optional chance to say its
        // RAM, processor or drive has been changed since it shipped. The step skips itself when the model
        // has only one of each, so nothing extra is asked of a device that cannot be configured.
        step: 'components',
        error: undefined,
      }

    case 'components':
      // Answering "as listed" is answering: the step is done either way, so the sale moves on.
      return {
        ...state,
        answers: {
          ...state.answers,
          ramId: action.ramId,
          processorId: action.processorId,
          hardDriveId: action.hardDriveId,
          screenSizeId: action.screenSizeId,
        },
        quote: undefined,
        pricedSelection: undefined,
        step: 'age',
        error: undefined,
      }

    case 'age':
      return {
        ...state,
        answers: { ...state.answers, durationTimeId: action.durationTimeId },
        quote: undefined,
        pricedSelection: undefined,
        step: 'condition',
        error: undefined,
      }

    case 'conditions':
      return {
        ...state,
        answers: { ...state.answers, conditionIds: action.ids },
        quote: undefined,
        pricedSelection: undefined,
        step: 'faults',
        error: undefined,
      }

    case 'faults':
      return {
        ...state,
        answers: { ...state.answers, faultIds: action.ids },
        quote: undefined,
        pricedSelection: undefined,
        step: 'accessories',
        error: undefined,
      }

    case 'accessories':
      return {
        ...state,
        // Answered, including "I have none of them": an empty list is an answer, and the flag is what
        // tells the valuation service to treat the missing ones as missing.
        answers: { ...state.answers, accessoryIds: action.ids, accessoriesAssessed: true },
        quote: undefined,
        pricedSelection: undefined,
        // Straight to the price. The pickup location is deliberately NOT asked here: at this moment the
        // seller is still a guest, so the question cannot be answered from the address already on their
        // account - it can only be asked again. A returning customer was therefore typing a pincode
        // Cashit already had on file. It is settled once, at confirmation, where their saved addresses
        // are on screen and a new one can be entered in full.
        step: 'quote',
        error: undefined,
      }

    case 'pincode': {
      // A pincode ANSWERS the pincode question and moves the sale on. Arriving at any later step it is
      // new information about the same sale - the customer correcting their area, or the assistant looking
      // one up mid-conversation - and it must not drag the sale backwards.
      //
      // It used to force step:'quote' unconditionally. After a quote existed that put the sale back on a
      // step whose only job is to fetch a quote, the fetch was skipped because one was already there, and
      // nothing ever moved it on again: the verify-your-number form disappeared and the sale was stuck
      // with no error and no way forward.
      //
      // The pickup area does not change the price (no CityId is sent with the quote), so an established
      // quote stays valid and is deliberately kept.
      const answersThePincodeQuestion = state.step === 'pincode'
      return {
        ...state,
        pincode: action.pincode,
        pickup: action.pickup,
        step: answersThePincodeQuestion ? 'quote' : state.step,
        error: undefined,
      }
    }

    case 'quote':
      // The selection is stored WITH the quote and replaced WITH it, so the two can never be out of
      // step: there is no moment where a price is on screen beside the answers of a different one.
      return { ...state, quote: action.quote, pricedSelection: action.selection, step: 'identify', error: undefined }

    case 'step':
      return { ...state, step: action.step, error: undefined }

    case 'idempotencyKey':
      return { ...state, idempotencyKey: action.key }

    case 'lead':
      return { ...state, leadNumber: action.leadNumber, step: 'done', error: undefined }

    case 'error':
      return { ...state, error: action.message }

    default:
      return state
  }
}

/** 0-100, for the progress bar. The labels match the design's wording. */
export const progressOf = (step: SaleStep): { percent: number; label: string } =>
  ({
    category: { percent: 6, label: 'Choose a category' },
    model: { percent: 18, label: 'Find your exact model' },
    configuration: { percent: 28, label: 'Confirm the configuration' },
    components: { percent: 34, label: 'Check its actual specification' },
    age: { percent: 42, label: 'How old is it' },
    condition: { percent: 54, label: 'Describe its condition' },
    faults: { percent: 64, label: 'Anything not working' },
    accessories: { percent: 72, label: 'What you still have' },
    pincode: { percent: 82, label: 'Enter a pickup pincode' },
    quote: { percent: 90, label: 'Getting your Cashit price' },
    identify: { percent: 94, label: 'Verify your mobile number' },
    confirm: { percent: 97, label: 'Confirm your sale request' },
    done: { percent: 100, label: 'Sale request created' },
  })[step]

/** Rupees as the rest of Cashit writes them. No currency conversion, no rounding of any kind. */
export const rupees = (amount: number): string =>
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount)}`

/**
 * The quote request for the current state, or null when something required has not been answered.
 * Built in ONE place so the price the customer is shown and the price the lead is created at are
 * derived from exactly the same answers.
 */
export function quoteRequestFor(state: SaleState, assessment?: DeviceAssessment) {
  if (!state.device) return null
  const { answers } = state
  return {
    deviceConfigId: state.device.deviceConfigId,
    deviceVariantId: answers.deviceVariantId ?? assessment?.selectedDeviceVariantId,
    durationTimeId: answers.durationTimeId,
    deviceConditionIds: answers.conditionIds,
    functionalIssueIds: answers.faultIds,
    accessoryIds: answers.accessoryIds,
    accessoriesAssessed: answers.accessoriesAssessed,
    // Sent only when the customer actually changed something. Sending the configuration's own components
    // back would flag every laptop lead as a custom configuration for a vendor to re-verify.
    ramId: answers.ramId,
    processorId: answers.processorId,
    hardDriveId: answers.hardDriveId,
    screenSizeId: answers.screenSizeId,
  }
}
